/**
 * Contact Address Book (Phase 1) — API client. Backend: sendlinxapi ContactController.
 * Set REACT_APP_CONTACTS_MOCK=true to run the UI on in-memory sample data. Never in production.
 */
import axios from "axios";
import Config from "../../../../../Config";

export const isContactsMockMode = () => {
    try{ return process.env.REACT_APP_CONTACTS_MOCK === "true"; }catch(e){ }
    return false;
};

const unwrap = (res) => {
    const data = res && res.data ? res.data : {};
    if(data.code === undefined || data.code === 200){
        return data.body !== undefined ? data.body : data;
    }
    throw (data.body || { message: "Invalid response from api." });
};
const unwrapError = (err) => {
    if(err && err.response && typeof(err.response) === "object" && err.response.data){
        return err.response.data.body || err.response.data;
    }
    return err;
};
const request = (method, path, { data = null, params = null, source = null } = {}) => {
    const options = { url: Config.getApiUrl()+path, method: method, headers: Config.getApiHeaders() };
    if(data !== null){ options.data = JSON.stringify(data); }
    if(params !== null){ options.params = params; }
    if(source !== null){ options.cancelToken = source.token; }
    return axios(options).then(unwrap).catch(err => { throw unwrapError(err); });
};

/* ---------------- mock store ---------------- */
const mock = {
    seq: 100,
    contacts: [
        { id: 1, name: "ABC Inc. - AP", firstName: "", lastName: "", company: "ABC Inc.", email: "ap@abcinc.com", favourite: true, groupIds: [1] },
        { id: 2, name: "John Smith", firstName: "John", lastName: "Smith", company: "Acme", email: "john.smith@acme.ca", favourite: false, groupIds: [1] },
        { id: 3, name: "Mary Johnson", firstName: "Mary", lastName: "Johnson", company: "Acme", email: "mary@acme.ca", favourite: false, groupIds: [1] },
        { id: 4, name: "Robert Davis", firstName: "Robert", lastName: "Davis", company: "", email: "robert.davis@acme.ca", favourite: true, groupIds: [1] },
        { id: 5, name: "Sales Team", firstName: "", lastName: "", company: "Acme", email: "sales@acme.ca", favourite: false, groupIds: [1] },
        { id: 6, name: "Accounts Payable", firstName: "", lastName: "", company: "", email: "ap@xyzcorp.com", favourite: false, groupIds: [2] },
        { id: 7, name: "Kevin Wilson", firstName: "Kevin", lastName: "Wilson", company: "XYZ Corp", email: "kevin@xyzcorp.com", favourite: false, groupIds: [2] },
        { id: 8, name: "Sarah Brown", firstName: "Sarah", lastName: "Brown", company: "", email: "sarah@contoso.com", favourite: true, groupIds: [3] },
        { id: 9, name: "", firstName: "", lastName: "", company: "", email: "jason.park@metrogroup.com", favourite: false, groupIds: [] }
    ],
    // Recent recipients (email, last used). Several are not contacts yet.
    recent: [
        ["michael.carter@buildright.ca", "2026-10-19 09:12:00"], ["emily.rogers@innovate.ca", "2026-09-18 14:40:00"],
        ["robert.davis@acme.ca", "2026-09-02 11:00:00"], ["alex.chen@acmecorp.com", "2025-01-07 10:00:00"],
        ["sarah.johnson@globaltech.com", "2025-07-09 16:30:00"], ["jason.park@metrogroup.com", "2024-07-02 08:15:00"],
        ["kevin@xyzcorp.com", "2024-06-30 13:00:00"], ["robert.thompson@industrial.ca", "2024-06-15 09:45:00"],
        ["karen.lee@solutionsinc.com", "2024-06-10 12:20:00"], ["ap@abcinc.com", "2024-05-28 10:05:00"]
    ],
    groups: [
        { id: 1, name: "Customers", description: "", favourite: false },
        { id: 2, name: "Vendors", description: "", favourite: false },
        { id: 3, name: "Internal", description: "", favourite: true }
    ]
};
const delay = (v) => new Promise(resolve => setTimeout(() => resolve(JSON.parse(JSON.stringify(v))), 150));
const fail = (message) => new Promise((resolve, reject) => setTimeout(() => reject({ message }), 150));
const mockGroups = () => mock.groups.map(g => {
    const memberIds = mock.contacts.filter(c => c.groupIds.includes(g.id)).map(c => c.id);
    return { ...g, memberIds, memberCount: memberIds.length };
});
const displayName = (first, last) => [String(first || "").trim(), String(last || "").trim()].filter(Boolean).join(" ");
const mockContact = (c) => ({ id: c.id, name: c.name, firstName: c.firstName || "", lastName: c.lastName || "", company: c.company || "", email: c.email, favourite: c.favourite,
    groups: mock.groups.filter(g => c.groupIds.includes(g.id)).map(g => ({ id: g.id, name: g.name })) });
const mockList = () => ({ contacts: mock.contacts.map(mockContact), groups: mockGroups() });

const ContactsApi = {
    list(source = null){
        if(isContactsMockMode()){ return delay(mockList()); }
        return request("GET", "user/contacts", { source });
    },
    /**
     * Recent recipients with server-side search and paging.
     * query: { search, status: "all"|"in"|"not", sort: "lastUsed"|"email"|"name", dir: "asc"|"desc", page, limit }
     * Returns { recent: [...], total, page, limit, hasMore }.
     */
    recent(query = {}, source = null){
        const params = { search: query.search || "", status: query.status || "all", sort: query.sort || "lastUsed",
            dir: query.dir || "desc", page: query.page || 1, limit: query.limit || 50 };
        if(isContactsMockMode()){
            const q = String(params.search).trim().toLowerCase();
            let rows = mock.recent.map(([email, lastUsed]) => {
                const c = mock.contacts.find(x => x.email === email);
                return { email, lastUsed, contactId: c ? c.id : null, inContacts: !!c, name: c ? c.name : "",
                    firstName: c ? c.firstName : "", lastName: c ? c.lastName : "", company: c ? c.company : "", favourite: c ? c.favourite : false };
            }).filter(r => (params.status === "all" || (params.status === "in") === r.inContacts) &&
                (!q || r.email.includes(q) || r.name.toLowerCase().includes(q) || r.company.toLowerCase().includes(q)));
            const key = params.sort === "email" ? (r => r.email) : params.sort === "name" ? (r => (r.name || r.email).toLowerCase()) : (r => r.lastUsed);
            const dir = params.dir === "asc" ? 1 : -1;
            rows.sort((a, b) => (key(a) < key(b) ? -dir : key(a) > key(b) ? dir : 0));
            const start = (params.page - 1) * params.limit;
            return delay({ recent: rows.slice(start, start + params.limit), total: rows.length, page: params.page,
                limit: params.limit, hasMore: start + params.limit < rows.length });
        }
        return request("GET", "user/contacts/recent", { params, source });
    },
    /** Review & Save Contacts: de-duplicated rows for the selected emails, with existing-contact status. Writes nothing. */
    previewRecent(emails){
        if(isContactsMockMode()){
            const seen = new Set();
            const entries = [];
            const counts = { selected: 0, new: 0, existing: 0, invalid: 0 };
            emails.forEach(original => {
                const email = String(original || "").trim().toLowerCase();
                if(!email || seen.has(email)){ return; }
                seen.add(email);
                counts.selected++;
                const c = mock.contacts.find(x => x.email === email);
                const known = c || mock.recent.some(([e]) => e === email);
                if(!known){ counts.invalid++; entries.push({ email, original, status: "invalid", contact: null, groupIds: [], error: "This address is not in your recent recipients." }); return; }
                if(c){ counts.existing++; } else { counts.new++; }
                entries.push({ email, original, status: c ? "existing" : "new", error: null, groupIds: c ? c.groupIds.slice() : [],
                    contact: c ? { id: c.id, name: c.name, firstName: c.firstName, lastName: c.lastName, company: c.company, favourite: c.favourite } : null });
            });
            return delay({ entries, counts, groups: mockGroups() });
        }
        return request("POST", "user/contacts/recent/preview", { data: { emails } });
    },
    /**
     * Save Contacts in one transaction.
     * payload: { contacts: [{ email, firstName, lastName, company, updateDetails }], groupIds: [], newGroupName, favourite }
     * Errors: 422 { message, rowErrors: { email: { field: message } } } or 409 { field: "newGroupName", existingGroup }.
     */
    bulkSave(payload){
        if(isContactsMockMode()){
            const name = String(payload.newGroupName || "").trim();
            const clash = name && mock.groups.find(g => g.name.toLowerCase() === name.toLowerCase());
            if(clash){
                return new Promise((resolve, reject) => setTimeout(() => reject({ message: "You already have a group named \""+clash.name+"\". Use that group or choose another name.",
                    field: "newGroupName", existingGroup: { id: clash.id, name: clash.name } }), 150));
            }
            const rowErrors = {};
            payload.contacts.forEach(r => {
                if(String(r.firstName || "").length > 75){ rowErrors[r.email] = { firstName: "Use at most 75 characters." }; }
            });
            if(Object.keys(rowErrors).length){
                return new Promise((resolve, reject) => setTimeout(() => reject({ message: "Rows need attention. Nothing was saved.", rowErrors }), 150));
            }
            const summary = { createdCount: 0, existingCount: 0, updatedCount: 0, membershipsAdded: 0, favouritesAdded: 0, groupCreated: null, results: [] };
            const groupIds = (payload.groupIds || []).slice();
            if(name){ const g = { id: ++mock.seq, name, description: "", favourite: false }; mock.groups.push(g); groupIds.push(g.id); summary.groupCreated = { id: g.id, name }; }
            payload.contacts.forEach(r => {
                const email = String(r.email).trim().toLowerCase();
                let c = mock.contacts.find(x => x.email === email);
                let outcome = "existing";
                if(!c){
                    c = { id: ++mock.seq, email, firstName: r.firstName || "", lastName: r.lastName || "", company: r.company || "",
                        name: displayName(r.firstName, r.lastName), favourite: false, groupIds: [] };
                    mock.contacts.push(c);
                    outcome = "created";
                }else if(r.updateDetails){
                    Object.assign(c, { firstName: r.firstName || "", lastName: r.lastName || "", company: r.company || "", name: displayName(r.firstName, r.lastName) });
                    outcome = "updated";
                }
                summary[outcome === "created" ? "createdCount" : "existingCount"]++;
                if(outcome === "updated"){ summary.updatedCount++; }
                let added = 0;
                groupIds.forEach(id => { if(!c.groupIds.includes(id)){ c.groupIds.push(id); added++; } });
                summary.membershipsAdded += added;
                if(payload.favourite && !c.favourite){ c.favourite = true; summary.favouritesAdded++; }
                summary.results.push({ email, contactId: c.id, outcome, membershipsAdded: added });
            });
            return delay({ ...summary, groups: mockGroups() });
        }
        return request("POST", "user/contacts/bulk-save", { data: payload });
    },
    saveContact(contact){
        const data = { firstName: contact.firstName || "", lastName: contact.lastName || "", company: contact.company || "",
            email: contact.email, groupIds: contact.groupIds || [] };
        if(isContactsMockMode()){
            const email = String(contact.email || "").trim().toLowerCase();
            if(mock.contacts.some(c => c.email === email && c.id !== contact.id)){ return fail("A contact with this email already exists."); }
            let row = mock.contacts.find(c => c.id === contact.id);
            if(!row){ row = { id: ++mock.seq, favourite: false }; mock.contacts.push(row); }
            Object.assign(row, { firstName: data.firstName, lastName: data.lastName, company: data.company,
                name: displayName(data.firstName, data.lastName), email, groupIds: data.groupIds });
            return delay({ contact: mockContact(row) });
        }
        return contact.id ? request("PUT", "user/contacts/"+contact.id, { data }) : request("POST", "user/contacts", { data });
    },
    deleteContact(id){
        if(isContactsMockMode()){ mock.contacts = mock.contacts.filter(c => c.id !== id); return delay({ deleted: [id] }); }
        return request("DELETE", "user/contacts/"+id);
    },
    favouriteContact(id, favourite){
        if(isContactsMockMode()){
            const row = mock.contacts.find(c => c.id === id); if(row){ row.favourite = !!favourite; }
            return delay({ contact: row ? mockContact(row) : null });
        }
        return request("POST", "user/contacts/"+id+"/favourite", { data: { favourite: favourite ? 1 : 0 } });
    },
    importContacts(contacts, createGroups){
        if(isContactsMockMode()){
            const summary = { added: 0, skipped: 0, failed: 0, groupsCreated: 0, errors: [] };
            contacts.forEach(r => {
                const email = String(r.email || "").trim().toLowerCase();
                if(mock.contacts.some(c => c.email === email)){ summary.skipped++; return; }
                let groupIds = [];
                if(r.group){
                    let g = mock.groups.find(x => x.name.toLowerCase() === r.group.toLowerCase());
                    if(!g && createGroups){ g = { id: ++mock.seq, name: r.group, description: "", favourite: false }; mock.groups.push(g); summary.groupsCreated++; }
                    if(g){ groupIds = [g.id]; }
                }
                mock.contacts.push({ id: ++mock.seq, name: r.name, firstName: "", lastName: "", company: "", email, favourite: false, groupIds }); summary.added++;
            });
            return delay(summary);
        }
        return request("POST", "user/contacts/import", { data: { contacts, createGroups: createGroups ? 1 : 0 } });
    },
    saveGroup(group){
        const data = { name: group.name, description: group.description || "", favourite: group.favourite ? 1 : 0, contactIds: group.contactIds || [] };
        if(isContactsMockMode()){
            const name = String(group.name || "").trim();
            if(mock.groups.some(g => g.name.toLowerCase() === name.toLowerCase() && g.id !== group.id)){ return fail("You already have a group with this name."); }
            let row = mock.groups.find(g => g.id === group.id);
            if(!row){ row = { id: ++mock.seq }; mock.groups.push(row); }
            Object.assign(row, { name, description: data.description, favourite: !!group.favourite });
            mock.contacts.forEach(c => {
                c.groupIds = c.groupIds.filter(id => id !== row.id);
                if(data.contactIds.includes(c.id)){ c.groupIds.push(row.id); }
            });
            return delay({ group: { id: row.id }, groups: mockGroups() });
        }
        return group.id ? request("PUT", "user/contact-groups/"+group.id, { data }) : request("POST", "user/contact-groups", { data });
    },
    deleteGroup(id){
        if(isContactsMockMode()){
            mock.groups = mock.groups.filter(g => g.id !== id);
            mock.contacts.forEach(c => { c.groupIds = c.groupIds.filter(g => g !== id); });
            return delay({ deleted: id, groups: mockGroups() });
        }
        return request("DELETE", "user/contact-groups/"+id);
    },
    favouriteGroup(id, favourite){
        if(isContactsMockMode()){
            const row = mock.groups.find(g => g.id === id); if(row){ row.favourite = !!favourite; }
            return delay({ groups: mockGroups() });
        }
        return request("POST", "user/contact-groups/"+id+"/favourite", { data: { favourite: favourite ? 1 : 0 } });
    }
};

export default ContactsApi;
